<template>
  <div style="display: none;" />
</template>

<script setup lang="ts">
import { usePageData } from '@/common/composables/usePageData'
import { readPageBoundIdentityFieldMap } from '@/common/utils/documentTransferIdentity'
import {
  ElementContextKey,
  FormProviderKey,
  SwingTilesRowScopeKey,
} from '@/components/platform/injection-keys'
import { resolveTilesRowScopedRegistryName } from '@/common/utils/resolveTilesRowScopedRegistryName'
import {
  extractIdentificatorRefillField,
  resolveIdentificatorGetDataValue,
} from '@/common/utils/identificatorGetData'
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'

interface Props {
  data: any
}

const props = defineProps<Props>()

const formProvider = inject(FormProviderKey, null)
const elementContext = inject(ElementContextKey, { formId: '', panelId: '' })
const swingTilesRowScope = inject(SwingTilesRowScopeKey, null)
const { pageFields, pageDataSlot, pageData } = usePageData()

// Computed properties
const formId = computed(() => String(elementContext?.formId ?? '').trim())
const panelId = computed(() => String(elementContext?.panelId ?? '').trim())
const elementId = computed(() => props.data?._attributes?.id ?? null)
const name = computed(() => String(props.data?.name?._text ?? '').trim())
const registrationName = computed(() =>
  resolveTilesRowScopedRegistryName(swingTilesRowScope, name.value),
)
const value = computed(() => {
  if (!name.value) return undefined
  if (swingTilesRowScope?.getRowFieldValue) {
    const fromRow = swingTilesRowScope.getRowFieldValue(name.value)
    if (fromRow !== undefined && fromRow !== null) return fromRow
  }
  const fromFields = pageFields.value?.[name.value]
  if (fromFields !== undefined && fromFields !== null) return fromFields
  return readPageBoundIdentityFieldMap(pageData.value)[name.value]
})

const formPath = computed(() => ({
  elementId: elementId.value,
  panelId: panelId.value,
  formId: formId.value,
}))

const lastRefillField = ref<unknown>(undefined)

watch(value, () => {
  if (!swingTilesRowScope && lastRefillField.value !== undefined) return
  updateValue()
})

const updateValue = () => {
  if (!formProvider || value.value === undefined || !name.value || !formId.value || !panelId.value || !elementId.value) return

  formProvider.setValue({
    value: { [registrationName.value || name.value]: value.value },
    ...formPath.value,
    skipStore: true,
    tilesRowScope: swingTilesRowScope ?? undefined,
  })
}

const refill = (data: any) => {
  if (swingTilesRowScope) {
    lastRefillField.value = undefined
  } else {
    lastRefillField.value = extractIdentificatorRefillField(
      data,
      name.value,
      registrationName.value,
    )
  }
  if (!formProvider || !formId.value || !panelId.value || !elementId.value) return
  formProvider.setValue({
    value: data,
    ...formPath.value,
    skipStore: true,
    tilesRowScope: swingTilesRowScope ?? undefined,
  })
  if (swingTilesRowScope) {
    updateValue()
  }
}

const readRegistryField = () => {
  if (!formProvider || !formId.value || !panelId.value || !elementId.value) return undefined
  const fromForms = formProvider.getForm({
    formId: formId.value,
    panelId: panelId.value,
    elementId: elementId.value,
    pageDataSlot: pageDataSlot.value || undefined,
    flatten: false,
  })
  return fromForms?.[name.value] ?? fromForms?.[registrationName.value]
}

/**
 * Swing Identificator.getData after fill: refill / element registry, then pageFields.
 * Не вызывать getForm({ panelId }) — panel getForm сам зовёт этот hook.
 */
const getIdentificatorData = () => {
  if (!name.value) return {}
  const field = resolveIdentificatorGetDataValue({
    refillValue: lastRefillField.value,
    registryValue: readRegistryField(),
    pageFieldValue: value.value,
  })
  if (field === undefined) return {}
  return { [name.value]: field }
}

onMounted(() => {
  if (!formProvider || !name.value || !formId.value || !panelId.value || !elementId.value) return

  formProvider.registerElement({
    name: registrationName.value || name.value,
    registryName: registrationName.value || undefined,
    element: props.data,
    type: 'IDENTIFICATOR',
    elementId: elementId.value,
    panelId: panelId.value,
    formId: formId.value,
    ctx: {
      ...(swingTilesRowScope ? { tilesRowScope: swingTilesRowScope } : {}),
      pageDataSlot: pageDataSlot.value || undefined,
    },
    hooks: {
      refill,
      getData: getIdentificatorData,
      getFullData: getIdentificatorData,
    },
  })

  updateValue()
})

onUnmounted(() => {
  if (!formProvider || !name.value || !formId.value || !panelId.value || !elementId.value) return
  formProvider.unRegisterElement({
    panelId: panelId.value,
    formId: formId.value,
    elementId: elementId.value,
    fieldName: registrationName.value || name.value,
    registryName: registrationName.value || undefined,
  })
})
</script>
